import React, { useState } from 'react';
import { Box, Button, Typography, Tooltip, Divider, Grid } from '@mui/material';
import { Breadcrumb } from 'src/components/Breadcrumb';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { ConfigurationHeader, HeaderFieldType } from '../ConfigurationHeader';
import { MessageTemplatesSection } from '../sections';
import { JsonPreviewModal } from '../JsonPreviewModal';
import {
  useCampaignConfigState,
  useMessageTemplates,
  useConfigOptions,
  useCampaignConfigLoadSave,
  SelectedFieldType,
} from '../../hooks';
import { getSectionsForType, typeHasMessageTemplates, SECTION_COMPONENTS } from '../../registry';

interface CampaignConfigBuilderProps {
  configId?: string;
}

/**
 * Thin container: wires the state/options/load-save hooks together and renders
 * the header, the type-specific sections (driven by the registry), the message
 * templates editor, and the save/preview footer. All business logic lives in
 * hooks + pure helpers.
 */
export const CampaignConfigBuilder: React.FC<CampaignConfigBuilderProps> = ({ configId }) => {
  const { config, setConfig, selectedField, setSelectedField, updateConfig } = useCampaignConfigState();
  const { selectedTemplateId, setSelectedTemplateId, addTemplate, updateTemplate, deleteTemplate, reorder } =
    useMessageTemplates(setConfig);
  const options = useConfigOptions(config.type);
  const { isLoading, isSaving, showValidation, isValid, validationErrors, save, countCampaignsUsingConfig } =
    useCampaignConfigLoadSave({
      configId,
      config,
      setConfig,
    });
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);

  const handleSave = async () => {
    if (!configId) {
      save();
      return;
    }
    setIsCheckingUsage(true);
    try {
      const count = await countCampaignsUsingConfig();
      if (count > 0) {
        openDialog({
          title: 'Update Campaign Configuration',
          content: `This config is used by ${count} campaigns and some fields may no longer be editable.`,
          onConfirm: save,
          confirmButtonText: 'Update',
          confirmButtonColor: 'primary',
          cancelButtonVariant: 'outlined',
        });
        return;
      }
      save();
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Campaigns', path: '/admin/campaigns' },
    { label: configId ? 'Edit Campaign Configuration' : 'Create Campaign Configuration', isActive: true },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography>Loading campaign configuration...</Typography>
      </Box>
    );
  }

  const sectionKeys = getSectionsForType(config.type);
  const showMessageTemplates = config.type !== 'custom' && typeHasMessageTemplates(config.type);

  return (
    <>
      <Box overflow="auto" display="flex" flexDirection="column">
        <Box p={2} pb={0} flexShrink={0}>
          <Breadcrumb items={breadcrumbItems} />
        </Box>

        <Box pt={0} flexShrink={0}>
          <ConfigurationHeader
            config={config}
            onConfigUpdate={updateConfig}
            selectedField={selectedField as HeaderFieldType}
            onFieldSelect={field => setSelectedField(field)}
            categoriesOptions={options.categoriesOptions}
            showValidation={showValidation}
            isEditing={!!configId}
          />
        </Box>

        {config.type !== 'custom' && sectionKeys.length > 0 && (
          <Box px={2}>
            {/* columnSpacing only: vertical spacing's negative margin would eat the
                10px gap below the description for sections that zero their top padding. */}
            <Grid container columnSpacing={2}>
              {sectionKeys.map(key => {
                const Section = SECTION_COMPONENTS[key];
                return (
                  <Section
                    key={key}
                    config={config}
                    onConfigUpdate={updateConfig}
                    options={{
                      groups: options.groups,
                      keywords: options.keywords,
                      appointmentTypes: options.appointmentTypes,
                      contactDateTypes: options.contactDateTypes,
                    }}
                    showValidation={showValidation}
                    selected={selectedField === (key as SelectedFieldType)}
                    onSelect={() => setSelectedField(key as SelectedFieldType)}
                    customFields={options.availableCustomFields}
                  />
                );
              })}
            </Grid>
          </Box>
        )}

        {showMessageTemplates && (
          <>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Box flexShrink={0} p={2} pt={0.5} display="flex" flexDirection="column">
              <MessageTemplatesSection
                templates={config.messageTemplates}
                campaignType={config.type}
                customFields={options.availableCustomFields}
                metadataTypes={options.metadataTypes}
                selectedTemplateId={selectedTemplateId}
                onSelectTemplate={setSelectedTemplateId}
                onAddTemplate={addTemplate}
                onUpdateTemplate={updateTemplate}
                onDeleteTemplate={deleteTemplate}
                onReorder={reorder}
                optOut={{
                  active: config.opt_out_message?.active ?? false,
                  value: config.opt_out_message?.value ?? '',
                }}
                onOptOutToggle={active =>
                  updateConfig({ opt_out_message: { ...config.opt_out_message, active, visible: active } })
                }
                onOptOutValueChange={value =>
                  updateConfig({ opt_out_message: { ...config.opt_out_message, value } })
                }
                isPoll={!!config.isPoll}
              />
            </Box>
          </>
        )}

        <JsonPreviewModal open={jsonModalOpen} onClose={() => setJsonModalOpen(false)} config={config} isUpdate={!!configId} />
      </Box>

      <Box mt={2} pr={2.5} pb={2.5} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          disabled={!isValid}
          variant="outlined"
          onClick={() => setJsonModalOpen(true)}
          size="large"
          sx={{ minWidth: '120px' }}
        >
          View JSON
        </Button>
        <Tooltip
          title={
            !isValid && validationErrors.length > 0 ? (
              <Box>
                {validationErrors.map((error, index) => (
                  <Typography key={index} variant="caption">
                    • {error}
                  </Typography>
                ))}
              </Box>
            ) : (
              ''
            )
          }
          arrow
          placement="top"
        >
          <span>
            <Button variant="contained" onClick={handleSave} disabled={isSaving || isCheckingUsage || !isValid} size="large">
              {isSaving ? 'Saving...' : configId ? 'Update Config' : 'Create Config'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <ConfirmationDialog />
    </>
  );
};
